import axios from 'axios';
import _ from 'lodash';
import * as Z from 'zod';

import { getSetting } from './global-settings';

import appConfig from '../app-config';

type BrightcoveOptions = {
    accountId: string;
    policyKey: string;
};

const brightcoveVideoSchema = Z.object({
    httpsPoster: Z.string().optional(),
    poster: Z.string(),
    poster_sources: Z.object({
        src: Z.string(),
    }).array(),
}).nonstrict();

type BrightcoveVideo = Z.infer<typeof brightcoveVideoSchema>;

const getHeaders = (policyKey: string) => ({
    Accept: `application/json;pk=${policyKey}`,
});

const getUrl = (baseUrl: string, accountId: string, videoId: string) =>
    baseUrl.replace('{accountId}', accountId).replace('{videoId}', videoId);

const addHttpPoster = (data: BrightcoveVideo): BrightcoveVideo => {
    const posterSource = data.poster_sources.find(({ src }) =>
        src.toLowerCase().startsWith('https'),
    );

    if (!posterSource) {
        return data;
    }

    const result = {
        ...data,
        httpsPoster: posterSource.src,
    };

    return result;
};

export const getVideo = (videoId: string): Promise<void | BrightcoveVideo> => {
    const brightcoveOptions = getSetting<BrightcoveOptions>('brightcove');

    if (!brightcoveOptions) {
        throw new Error('No brightcove options in globalSettings');
    }

    const brightcoveUrl: string = _.get(
        appConfig,
        'urls.api.brightcove.apiUrl',
    );

    if (!brightcoveUrl) {
        throw new Error('No brightcove URL in appConfig');
    }

    const { accountId, policyKey } = brightcoveOptions;

    return axios({
        url: getUrl(brightcoveUrl, accountId, videoId),
        headers: getHeaders(policyKey),
    })
        .then(({ data }) => brightcoveVideoSchema.parseAsync(data))
        .then(addHttpPoster);
};
